import React, { useMemo } from 'react'
import { Form, Radio as RadioAntd } from 'antd'
import { RadioGroupProps, RadioChangeEvent } from 'antd/lib/radio'
import { FormItemProps } from 'antd/lib/form'
import dp from 'dot-prop-immutable'

import { useT } from 'src/core/hooks/use-translation'

export interface Option {
  disabled?: boolean
  label: string
  value: string
}

type Props = Omit<RadioGroupProps, 'options'> &
  Pick<FormItemProps, 'required'> & {
    dataTestId: string
    formikProps: any
    options: Option[]
  }

function Radio({
  dataTestId = 'radio-list',
  formikProps,
  name,
  onChange,
  options,
  required = true,
  size = 'large',
  ...props
}: Props) {
  const { t } = useT()

  const error = useMemo(() => {
    return dp.get(formikProps.errors, name)
  }, [formikProps.errors, name])

  const touched = useMemo(() => {
    return dp.get(formikProps.touched, name)
  }, [formikProps.touched, name])

  const value = useMemo(() => {
    return dp.get(formikProps.values, name)
  }, [formikProps.values, name])

  function handleChange(e: RadioChangeEvent) {
    if (onChange) {
      onChange(e)
      return
    }

    formikProps.setFieldValue(name, e.target.value)
  }

  return (
    <Form.Item
      data-test-id={`${dataTestId}-list`}
      colon={false}
      help={touched ? t(error) : ''}
      required={required}
      validateStatus={
        error && touched ? 'error' : !error && touched ? 'success' : ''
      }
      hasFeedback={required}
    >
      <RadioAntd.Group
        {...props}
        name={name}
        onChange={handleChange}
        size={size}
        value={value}
      >
        {options.map(option => (
          <RadioAntd
            data-test-id={`${dataTestId}-item-${option.value}`}
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {t(option.label)}
          </RadioAntd>
        ))}
      </RadioAntd.Group>
    </Form.Item>
  )
}

export default Radio
